import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { ShieldAlert, ShieldCheck, AlertOctagon, Lock, Loader2, FileSignature, ExternalLink } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

/**
 * Arms-Length Compliance Handoff Gateway.
 *
 * Wraps any transition from EchoSense (introducer-only) into the live
 * trading/investment side — currently the Deal Room and, once keys are
 * provisioned, the funding flow.
 *
 * Records audit-grade consent server-side (IP, UA, typed signature,
 * timestamp, policy version) before invoking `onProceed()`.
 *
 * Props:
 *   open      : boolean — controls visibility
 *   onClose   : () => void
 *   onProceed : () => void — called *after* server records the ack
 *   gateway   : "deal_room" | "paytiko_monaxa"
 *   context   : optional string — e.g. deal title or destination URL,
 *               shown for additional clarity
 */
const GATEWAY_COPY = {
  deal_room: {
    title: "Compliance Handoff — EchoSense Deal Room",
    eyebrow: "Arms-Length Notice",
    destination: "the EchoSense Deal Room and any third-party deal materials, partner data rooms, or trading-program documents linked from it",
    short: "Deal Room",
  },
  // Partner-platform handoff copy used when transitioning a member from the
  // EchoSense introducer portal into the live trading/funding environment.
  finbpm_monaxa: {
    title: "Compliance Handoff — Partner Platform",
    eyebrow: "Funding Transition",
    destination:
      "the Partner Platform, with funding processed through the regulated payments rail",
    short: "Partner Platform",
  },
  // Legacy alias — kept so any in-flight code paths still resolve.
  paytiko_monaxa: {
    title: "Compliance Handoff — Partner Platform",
    eyebrow: "Funding Transition",
    destination:
      "the Partner Platform, with funding processed through the regulated payments rail",
    short: "Partner Platform",
  },
};

const ComplianceHandoffGateway = ({
  open,
  onClose,
  onProceed,
  gateway = "deal_room",
  context,
}) => {
  const { user } = useAuth();
  const meta = GATEWAY_COPY[gateway] || GATEWAY_COPY.deal_room;

  const [typedName, setTypedName] = useState("");
  const [acks, setAcks] = useState({
    leaving: false,
    no_funds: false,
    no_profit: false,
    no_guarantee: false,
    own_risk: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [previouslySigned, setPreviouslySigned] = useState(null);

  useEffect(() => {
    if (!open) return;
    // Reset state every time the dialog opens
    setTypedName(user?.full_name || "");
    setAcks({ leaving: false, no_funds: false, no_profit: false, no_guarantee: false, own_risk: false });
    // Check if user has already signed this gateway — show a soft "re-confirm" path
    (async () => {
      try {
        const r = await api.get(`/compliance/handoff/status?gateway=${gateway}`);
        if (r.data?.acknowledged) setPreviouslySigned(r.data);
        else setPreviouslySigned(null);
      } catch (_) {
        setPreviouslySigned(null);
      }
    })();
  }, [open, gateway, user?.full_name]);

  const allAcked = Object.values(acks).every(Boolean);
  const nameOk = (typedName || "").trim().length >= 2;
  const canSubmit = allAcked && nameOk && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/compliance/handoff/acknowledge", {
        gateway,
        typed_full_name: typedName.trim(),
        ack_leaving_introducer_scope: acks.leaving,
        ack_no_funds_to_eqhosense:    acks.no_funds,
        ack_no_profit_participation:  acks.no_profit,
        ack_no_guarantee:             acks.no_guarantee,
        ack_own_risk:                 acks.own_risk,
      });
      toast.success("Compliance handshake recorded.");
      // Hand control back to caller (e.g. open the deal deck, redirect to Paytiko)
      try { onProceed && onProceed(); } catch (_) {}
    } catch (e) {
      const msg = e.response?.data?.detail || "Could not record acknowledgment.";
      toast.error(typeof msg === "string" ? msg : "Could not record acknowledgment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !submitting && onClose && onClose()}>
      <DialogContent
        data-testid="compliance-handoff-dialog"
        className="bg-[#0B1220] border border-amber-400/30 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-amber-400/30 to-amber-600/10 border border-amber-400/40 flex items-center justify-center shrink-0">
            <ShieldAlert className="text-amber-300" size={20} />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300/90 font-semibold mb-1">
              {meta.eyebrow}
            </p>
            <h2 className="text-2xl font-serif text-white leading-snug">
              {meta.title}
            </h2>
            {context && (
              <p className="text-sm text-slate-400 mt-1">
                Destination: <span className="text-slate-200 font-medium">{context}</span>
              </p>
            )}
          </div>
        </div>

        {/* Previously signed banner */}
        {previouslySigned && (
          <div
            data-testid="compliance-prev-signed"
            className="rounded-lg border border-emerald-500/30 bg-emerald-500/[0.06] p-3 mb-4 text-xs text-emerald-200"
          >
            <ShieldCheck className="inline-block mr-1.5 -mt-0.5" size={13} />
            You previously signed this handoff on{" "}
            <span className="font-mono">
              {new Date(previouslySigned.acknowledged_at).toLocaleString()}
            </span>
            . Re-confirming below refreshes the audit record before you proceed.
          </div>
        )}

        {/* The Notice */}
        <div
          data-testid="compliance-handoff-notice"
          className="rounded-lg border border-amber-400/15 bg-amber-400/[0.04] p-4 text-sm text-slate-200 leading-relaxed space-y-3"
        >
          <p>
            By continuing, you are <span className="text-amber-200 font-semibold">leaving the
            EchoSense Capital Club introducer portal</span> and entering{" "}
            <span className="text-amber-200 font-semibold">{meta.destination}</span>.
          </p>
          <p>
            EchoSense Capital Club is an <span className="text-white font-semibold">introducer-only</span>{" "}
            platform. We do not provide investment advice, hold or receive client funds, manage
            any trading account, issue tax forms, guarantee any return, or approve any account.
            All onboarding, funding instructions, account approvals, reporting, and tax
            documents are issued <span className="text-white font-semibold">directly by the
            regulated partner</span> (the partner trading platform and the regulated payments
            rail handling wire-transfer logistics; alongside any applicable fund administrator
            or approved third party), not by EchoSense.
          </p>
          <p className="border-l-2 border-amber-400/50 pl-3">
            <span className="text-amber-200 font-semibold">No profit participation.</span>{" "}
            EchoSense Capital Club receives no performance fee, profit share, carry, success
            fee, allocation kick-back, or any other form of profit participation in any
            trading program, hedge fund, managed account, or capital-markets opportunity
            surfaced past this gateway. Our compensation is limited to introducer fees
            disclosed during onboarding.
          </p>
          <p>
            <span className="text-amber-200 font-semibold">All risk is yours.</span> Trading,
            investing, and any allocation of capital carry the risk of partial or total loss.
            Past performance is never indicative of future results. Returns, profits, capital
            preservation, payouts, and timelines are not guaranteed by EchoSense, the partner,
            the payments rail, or any regulated partner.
          </p>
        </div>

        {/* Acks */}
        <div className="mt-5 space-y-3" data-testid="compliance-handoff-acks">
          <AckRow
            id="leaving"
            testId="ack-leaving"
            checked={acks.leaving}
            onChange={(v) => setAcks((a) => ({ ...a, leaving: v }))}
            label="I understand I am leaving the EchoSense Capital Club introducer portal and entering a third-party trading / investment environment."
          />
          <AckRow
            id="no_funds"
            testId="ack-no-funds"
            checked={acks.no_funds}
            onChange={(v) => setAcks((a) => ({ ...a, no_funds: v }))}
            label="I will never send funds to EchoSense Capital Club, any introducer, or any individual. I will only follow funding instructions issued in writing by the regulated partner."
          />
          <AckRow
            id="no_profit"
            testId="ack-no-profit"
            checked={acks.no_profit}
            onChange={(v) => setAcks((a) => ({ ...a, no_profit: v }))}
            label="I understand EchoSense Capital Club takes no profit participation, performance fee, carry, or trading share in any opportunity surfaced past this gateway."
          />
          <AckRow
            id="no_guarantee"
            testId="ack-no-guarantee"
            checked={acks.no_guarantee}
            onChange={(v) => setAcks((a) => ({ ...a, no_guarantee: v }))}
            label="I understand no return, profit, capital preservation, payout, or timeline is guaranteed. Past performance is never indicative of future results."
          />
          <AckRow
            id="own_risk"
            testId="ack-own-risk"
            checked={acks.own_risk}
            onChange={(v) => setAcks((a) => ({ ...a, own_risk: v }))}
            label="All trading, investment, and capital-allocation risk is mine alone. I accept full responsibility for any losses incurred."
          />
        </div>

        {/* Electronic Signature */}
        <div className="mt-5 rounded-lg border border-platinum/10 bg-navy-light/30 p-4">
          <Label className="text-[10px] uppercase tracking-[0.3em] text-slate-400 font-semibold flex items-center gap-1.5 mb-2">
            <FileSignature size={12} /> Electronic Signature
          </Label>
          <p className="text-xs text-slate-400 mb-2 leading-relaxed">
            Type your full legal name to sign this acknowledgment. This is captured with a
            timestamp, IP, and user-agent for the audit log.
          </p>
          <Input
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            placeholder="e.g. Marcus J. Thompson"
            className="bg-[#0F1424] border-platinum/15 text-white"
            data-testid="compliance-handoff-typed-name"
          />
        </div>

        {/* Action row */}
        <div className="mt-5 flex flex-col sm:flex-row gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => !submitting && onClose && onClose()}
            disabled={submitting}
            className="flex-1 border-platinum/20 text-slate-200 hover:bg-platinum/10 bg-transparent"
            data-testid="compliance-handoff-cancel"
          >
            <Lock size={14} className="mr-2" />
            Cancel — Stay In Portal
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className="flex-1 bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold disabled:opacity-40"
            data-testid="compliance-handoff-confirm"
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="mr-2 animate-spin" /> Recording…
              </>
            ) : (
              <>
                Acknowledge & Proceed to {meta.short}
                <ExternalLink size={13} className="ml-2" />
              </>
            )}
          </Button>
        </div>

        <p className="text-[10px] text-slate-500 leading-relaxed mt-4 text-center">
          Policy version 2026.07.24-v3 · EchoSense Capital Club acts solely as an Introducer.
          <br />
          Records retained for compliance audit.
        </p>
      </DialogContent>
    </Dialog>
  );
};

const AckRow = ({ id, testId, checked, onChange, label }) => (
  <label
    htmlFor={id}
    className="flex items-start gap-3 cursor-pointer rounded-md p-2.5 hover:bg-amber-400/[0.03] transition-colors"
    data-testid={`${testId}-row`}
  >
    <Checkbox
      id={id}
      checked={checked}
      onCheckedChange={(v) => onChange(!!v)}
      className="mt-0.5 border-amber-400/40 data-[state=checked]:bg-amber-400 data-[state=checked]:border-amber-400 data-[state=checked]:text-slate-900"
      data-testid={testId}
    />
    <span className="text-sm text-slate-200 leading-relaxed">{label}</span>
  </label>
);

export default ComplianceHandoffGateway;
